/**
 * useKeyboardShortcuts Hook
 *
 * Manages keyboard shortcuts for EEG viewer:
 * - Arrow keys for navigation
 * - Space for play/pause
 * - M for montage, F for filters
 * - +/- for sensitivity
 * - [/] for time scale
 * - 1-9 for presets
 * - Ctrl+Z/Y for undo/redo
 */

import { useEffect, useCallback, useRef } from "react";

export interface KeyboardShortcut {
  key: string;
  code?: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean; // For Mac Command key
  action: () => void;
  description: string;
  category: string;
  disabled?: boolean;
  allowInInput?: boolean;
  preventDefault?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  preventDefault?: boolean;
  shouldHandleEvent?: (event: KeyboardEvent) => boolean;
}

function isEditableTarget(target: HTMLElement | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable ||
    target.closest("[contenteditable='true']") !== null ||
    target.getAttribute("role") === "textbox"
  );
}

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
  preventDefault = true,
  shouldHandleEvent,
}: UseKeyboardShortcutsOptions) => {
  const shortcutsRef = useRef(shortcuts);

  // Update shortcuts ref when they change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  // Handle keydown event
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;
      if (shouldHandleEvent && !shouldHandleEvent(event)) return;

      const target = event.target as HTMLElement | null;
      const activeElement = document.activeElement as HTMLElement | null;
      const typingTarget =
        isEditableTarget(target) || isEditableTarget(activeElement);

      // Find matching shortcut
      const matchingShortcut = shortcutsRef.current.find((shortcut) => {
        if (shortcut.disabled) return false;

        if (shortcut.code && shortcut.code !== event.code) {
          return false;
        }

        const keyMatches =
          shortcut.key.toLowerCase() === event.key.toLowerCase();

        // Handle Ctrl/Meta (Command on Mac) - they are mutually exclusive
        const ctrlMatches = shortcut.ctrl
          ? event.ctrlKey && !event.metaKey // Ctrl only, not Meta
          : !event.ctrlKey; // No ctrl when not wanted

        const metaMatches = shortcut.meta
          ? event.metaKey && !event.ctrlKey // Meta only, not Ctrl
          : !event.metaKey; // No meta when not wanted

        const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatches = shortcut.alt ? event.altKey : !event.altKey;

        return (
          keyMatches && ctrlMatches && metaMatches && shiftMatches && altMatches
        );
      });

      if (matchingShortcut) {
        if (typingTarget && !matchingShortcut.allowInInput) {
          return;
        }

        const shouldPreventDefaultShortcut =
          matchingShortcut.preventDefault ?? preventDefault;
        if (shouldPreventDefaultShortcut) {
          event.preventDefault();
          event.stopPropagation();
        }
        matchingShortcut.action();
      }
    },
    [enabled, preventDefault, shouldHandleEvent],
  );

  // Attach/detach event listener
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, handleKeyDown]);

  return {
    shortcuts: shortcutsRef.current,
  };
};

/**
 * Predefined EEG viewer shortcuts
 */
export const createEEGShortcuts = (callbacks: {
  previousPage?: () => void;
  nextPage?: () => void;
  firstPage?: () => void;
  lastPage?: () => void;
  togglePlayPause?: () => void;
  increaseSpeed?: () => void;
  decreaseSpeed?: () => void;
  increaseSensitivity?: () => void;
  decreaseSensitivity?: () => void;
  increaseTimeScale?: () => void;
  decreaseTimeScale?: () => void;
  toggleMontageDropdown?: () => void;
  openFilters?: () => void;
  addAnnotation?: () => void;
  toggleGrid?: () => void;
  toggleMeasurementTools?: () => void;
  loadPreset?: (number: number) => void;
  undo?: () => void;
  redo?: () => void;
  showHelp?: () => void;
  openCommandPalette?: () => void;
  scrollUp?: () => void;
  scrollDown?: () => void;
}): KeyboardShortcut[] => {
  return [
    // Navigation
    {
      key: "ArrowLeft",
      action: callbacks.previousPage || (() => {}),
      description: "Previous page",
      category: "Navigation",
      disabled: !callbacks.previousPage,
    },
    {
      key: "ArrowRight",
      action: callbacks.nextPage || (() => {}),
      description: "Next page",
      category: "Navigation",
      disabled: !callbacks.nextPage,
    },
    {
      key: "Home",
      action: callbacks.firstPage || (() => {}),
      description: "First page",
      category: "Navigation",
      disabled: !callbacks.firstPage,
    },
    {
      key: "End",
      action: callbacks.lastPage || (() => {}),
      description: "Last page",
      category: "Navigation",
      disabled: !callbacks.lastPage,
    },
    {
      key: "ArrowUp",
      action: callbacks.scrollUp || (() => {}),
      description: "Scroll up",
      category: "Navigation",
      disabled: !callbacks.scrollUp,
    },
    {
      key: "ArrowDown",
      action: callbacks.scrollDown || (() => {}),
      description: "Scroll down",
      category: "Navigation",
      disabled: !callbacks.scrollDown,
    },
    {
      key: "PageUp",
      action: () => {
        if (callbacks.previousPage) {
          for (let i = 0; i < 10; i++) {
            callbacks.previousPage();
          }
        }
      },
      description: "Jump back 10 pages",
      category: "Navigation",
      disabled: !callbacks.previousPage,
    },
    {
      key: "PageDown",
      action: () => {
        if (callbacks.nextPage) {
          for (let i = 0; i < 10; i++) {
            callbacks.nextPage();
          }
        }
      },
      description: "Jump forward 10 pages",
      category: "Navigation",
      disabled: !callbacks.nextPage,
    },

    // Playback
    {
      key: " ",
      action: callbacks.togglePlayPause || (() => {}),
      description: "Play/Pause",
      category: "Playback",
      disabled: !callbacks.togglePlayPause,
    },
    {
      key: ">",
      shift: true,
      action: callbacks.increaseSpeed || (() => {}),
      description: "Increase speed",
      category: "Playback",
      disabled: !callbacks.increaseSpeed,
    },
    {
      key: "<",
      shift: true,
      action: callbacks.decreaseSpeed || (() => {}),
      description: "Decrease speed",
      category: "Playback",
      disabled: !callbacks.decreaseSpeed,
    },

    // Controls
    {
      key: "m",
      action: callbacks.toggleMontageDropdown || (() => {}),
      description: "Toggle montage",
      category: "Controls",
      disabled: !callbacks.toggleMontageDropdown,
    },
    {
      key: "f",
      action: callbacks.openFilters || (() => {}),
      description: "Toggle filters",
      category: "Controls",
      disabled: !callbacks.openFilters,
    },
    {
      key: "g",
      action: callbacks.toggleGrid || (() => {}),
      description: "Toggle grid",
      category: "Controls",
      disabled: !callbacks.toggleGrid,
    },
    {
      key: "r",
      action: callbacks.toggleMeasurementTools || (() => {}),
      description: "Toggle measurement tools",
      category: "Controls",
      disabled: !callbacks.toggleMeasurementTools,
    },

    // Annotations
    {
      key: "a",
      action: callbacks.addAnnotation || (() => {}),
      description: "Add annotation",
      category: "Annotations",
      disabled: !callbacks.addAnnotation,
    },

    // Sensitivity
    {
      key: "+",
      action: callbacks.increaseSensitivity || (() => {}),
      description: "Increase sensitivity",
      category: "Display",
      disabled: !callbacks.increaseSensitivity,
    },
    {
      key: "=",
      action: callbacks.increaseSensitivity || (() => {}),
      description: "Increase sensitivity (alternate)",
      category: "Display",
      disabled: !callbacks.increaseSensitivity,
    },
    {
      key: "-",
      action: callbacks.decreaseSensitivity || (() => {}),
      description: "Decrease sensitivity",
      category: "Display",
      disabled: !callbacks.decreaseSensitivity,
    },

    // Time scale
    {
      key: "]",
      action: callbacks.increaseTimeScale || (() => {}),
      description: "Increase time scale",
      category: "Display",
      disabled: !callbacks.increaseTimeScale,
    },
    {
      key: "[",
      action: callbacks.decreaseTimeScale || (() => {}),
      description: "Decrease time scale",
      category: "Display",
      disabled: !callbacks.decreaseTimeScale,
    },

    // Presets
    {
      key: "1",
      action: () => callbacks.loadPreset && callbacks.loadPreset(1),
      description: "Preset 1",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "2",
      action: () => callbacks.loadPreset && callbacks.loadPreset(2),
      description: "Preset 2",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "3",
      action: () => callbacks.loadPreset && callbacks.loadPreset(3),
      description: "Preset 3",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "4",
      action: () => callbacks.loadPreset && callbacks.loadPreset(4),
      description: "Preset 4",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "5",
      action: () => callbacks.loadPreset && callbacks.loadPreset(5),
      description: "Preset 5",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "6",
      action: () => callbacks.loadPreset && callbacks.loadPreset(6),
      description: "Preset 6",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "7",
      action: () => callbacks.loadPreset && callbacks.loadPreset(7),
      description: "Preset 7",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "8",
      action: () => callbacks.loadPreset && callbacks.loadPreset(8),
      description: "Preset 8",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },
    {
      key: "9",
      action: () => callbacks.loadPreset && callbacks.loadPreset(9),
      description: "Preset 9",
      category: "Presets",
      disabled: !callbacks.loadPreset,
    },

    // Undo/Redo
    {
      key: "z",
      ctrl: true,
      action: callbacks.undo || (() => {}),
      description: "Undo",
      category: "Edit",
      disabled: !callbacks.undo,
    },
    {
      key: "y",
      ctrl: true,
      action: callbacks.redo || (() => {}),
      description: "Redo",
      category: "Edit",
      disabled: !callbacks.redo,
    },
    {
      key: "z",
      ctrl: true,
      shift: true,
      action: callbacks.redo || (() => {}),
      description: "Redo (Alt)",
      category: "Edit",
      disabled: !callbacks.redo,
    },

    // Help and Commands
    {
      key: "?",
      shift: true,
      action: callbacks.showHelp || (() => {}),
      description: "Show help",
      category: "Help",
      disabled: !callbacks.showHelp,
    },
    {
      key: "k",
      ctrl: true,
      action: callbacks.openCommandPalette || (() => {}),
      description: "Command palette",
      category: "Help",
      disabled: !callbacks.openCommandPalette,
    },
    {
      key: "k",
      meta: true,
      action: callbacks.openCommandPalette || (() => {}),
      description: "Command palette (Mac)",
      category: "Help",
      disabled: !callbacks.openCommandPalette,
    },
  ];
};

/**
 * Format shortcut for display
 */
export const formatShortcut = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  if (shortcut.ctrl) parts.push("Ctrl");
  if (shortcut.shift) parts.push("Shift");
  if (shortcut.alt) parts.push("Alt");

  // Format key name
  let keyName = shortcut.key;
  if (keyName === " ") keyName = "Space";
  else if (keyName === "ArrowLeft") keyName = "←";
  else if (keyName === "ArrowRight") keyName = "→";
  else if (keyName === "ArrowUp") keyName = "↑";
  else if (keyName === "ArrowDown") keyName = "↓";
  else keyName = keyName.toUpperCase();

  parts.push(keyName);

  return parts.join("+");
};

/**
 * Group shortcuts by category
 */
export const groupShortcutsByCategory = (
  shortcuts: KeyboardShortcut[],
): Record<string, KeyboardShortcut[]> => {
  return shortcuts.reduce(
    (groups, shortcut) => {
      if (shortcut.disabled) return groups;

      const category = shortcut.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(shortcut);
      return groups;
    },
    {} as Record<string, KeyboardShortcut[]>,
  );
};

/**
 * Alias for createEEGShortcuts to maintain backward compatibility with tests
 */
export const createDefaultEEGShortcuts = createEEGShortcuts;
